import { map } from 'lodash-es';
import * as C from '../../constants';
import SkiSchoolService from '../../../../services/ski/ski-school-service';
import SkiService from '../../../../services/ski/ski-service';
import exceptionValidator from '../../../../utils/exceptionValidator';

export default {

  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.SKI_SCHOOL_RESET_STATE);
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  seasonsLoad({ commit }) {
    commit(C.SKI_SCHOOL_SEASONS_LOAD_PENDING);
    return SkiSchoolService.getSeasonsWithRelations()
      .then((res) => {
        commit(C.SKI_SCHOOL_SEASONS_LOAD_SUCCESS, res.data);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_SEASONS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<void>|Promise<T>}
   */
  seasonByIdLoad({ commit, state }, seasonId) {
    commit(C.SKI_SCHOOL_SEASON_LOAD_PENDING);
    if (seasonId === undefined) {
      const newSeasonItem = Object.assign({}, state.season.newSeasonItem);
      commit(C.SKI_SCHOOL_SEASON_LOAD_SUCCESS, newSeasonItem);
      return Promise.resolve();
    }
    return SkiSchoolService.getSeasonById(seasonId)
      .then((res) => {
        commit(C.SKI_SCHOOL_SEASON_LOAD_SUCCESS, res.data);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_SEASON_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<T>}
   */
  seasonWithRelationsBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_SCHOOL_SEASON_LOAD_PENDING);
    return SkiSchoolService.getSeasonWithRelationsBySeasonId(seasonId)
      .then((res) => {
        commit(C.SKI_SCHOOL_SEASON_LOAD_SUCCESS, res.data);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_SEASON_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setSeason({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEASON_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  seasonCreate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_SEASON_CREATE_PENDING);
    return SkiSchoolService.createSeason(payload)
      .then((response) => {
        const skiSchoolSeasonId = response.data.id;
        const newSeasonItem = Object.assign({}, payload);
        newSeasonItem.id = skiSchoolSeasonId;
        commit(C.SKI_SCHOOL_SEASON_CREATE_SUCCESS, newSeasonItem);
        dispatch('seasonByIdLoad', newSeasonItem.id);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_SEASON_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  seasonSave({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEASON_SAVE_PENDING);
    return SkiSchoolService.saveSeason(payload)
      .then(() => {
        commit(C.SKI_SCHOOL_SEASON_SAVE_SUCCESS, payload);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_SEASON_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  salesTypesLoad({ commit }) {
    commit(C.SKI_SCHOOL_SALES_TYPES_LOAD_PENDING);
    return SkiSchoolService.getSalesTypes()
      .then((res) => {
        commit(C.SKI_SCHOOL_SALES_TYPES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_SALES_TYPES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  lessonTypesLoad({ commit }) {
    commit(C.SKI_SCHOOL_LESSON_TYPES_LOAD_PENDING);
    return SkiSchoolService.getSkiSchoolLessonTypes()
      .then((res) => {
        commit(C.SKI_SCHOOL_LESSON_TYPES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_LESSON_TYPES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  skiTypesLoad({ commit }) {
    commit(C.SKI_SCHOOL_SKI_TYPES_LOAD_PENDING);
    return SkiSchoolService.getSkiSchoolSkiTypes()
      .then((res) => {
        commit(C.SKI_SCHOOL_SKI_TYPES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_SKI_TYPES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  areasBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_SCHOOL_AREAS_LOAD_PENDING);
    return SkiSchoolService.getAreasBySeasonId(seasonId)
      .then((res) => {
        commit(C.SKI_SCHOOL_AREAS_LOAD_SUCCESS, res.data);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_AREAS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonId
   * @param newItem
   * @returns {Promise<T>}
   */
  areaOnSeasonSave({ commit, dispatch }, { seasonId, newItem }) {
    commit(C.SKI_SCHOOL_AREAS_SAVE_PENDING);
    return SkiSchoolService.saveAreaOnSeason(seasonId, newItem.id)
      .then(() => {
        commit(C.SKI_SCHOOL_AREAS_SAVE_SUCCESS);
        dispatch('areasBySeasonIdLoad', seasonId);
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_AREAS_SAVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonId
   * @param areaId
   * @returns {Promise<T>}
   */
  areaOnSeasonDelete({ commit, dispatch }, { seasonId, areaId }) {
    commit(C.SKI_SCHOOL_AREAS_DELETE_PENDING);
    return SkiSchoolService.deleteAreaOnSeason(seasonId, areaId)
      .then(() => {
        commit(C.SKI_SCHOOL_AREAS_DELETE_SUCCESS);
        dispatch('areasBySeasonIdLoad', seasonId);
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_AREAS_DELETE_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  suppliersLoad({ commit }) {
    commit(C.SKI_SUPPLIERS_LOAD_PENDING);
    return SkiService.getActiveSkiSuppliers()
      .then((res) => {
        commit(C.SKI_SUPPLIERS_LOAD_SUCCESS, res.data);
      })
      .catch((error) => {
        commit(C.SKI_SUPPLIERS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  personCategoriesByIdLoad({ commit }, seasonId) {
    commit(C.SKI_SCHOOL_PERSON_CATEGORIES_LOAD_PENDING);
    return SkiSchoolService.getPersonCategoriesById(seasonId)
      .then((res) => {
        const personCategories = map(res.data, personCategory => ({
          id: personCategory.id,
          skiSchoolSeasonId: personCategory.skiSchoolSeasonId,
          name: personCategory.name,
          externalName: personCategory.externalName,
          ageFrom: personCategory.ageFrom,
          ageTo: personCategory.ageTo,
        }));
        commit(C.SKI_SCHOOL_PERSON_CATEGORIES_LOAD_SUCCESS, personCategories);
      })
      .catch((error) => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_SCHOOL_PERSON_CATEGORIES_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_SCHOOL_PERSON_CATEGORIES_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   */
  personCategoryAddNew({ commit }) {
    commit(C.SKI_SCHOOL_PERSON_CATEGORY_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setPersonCategory({ commit }, payload) {
    commit(C.SKI_SCHOOL_PERSON_CATEGORIES_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  personCategoryCreate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_PERSON_CATEGORY_CREATE_PENDING);
    return SkiSchoolService.createPersonCategory(payload)
      .then(() => {
        commit(C.SKI_SCHOOL_PERSON_CATEGORY_CREATE_SUCCESS);
        dispatch('personCategoriesByIdLoad', payload.skiSchoolSeasonId);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PERSON_CATEGORY_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  personCategorySave({ commit }, payload) {
    commit(C.SKI_SCHOOL_PERSON_CATEGORY_SAVE_PENDING);
    return SkiSchoolService.savePersonCategory(payload)
      .then(() => {
        commit(C.SKI_SCHOOL_PERSON_CATEGORY_SAVE_SUCCESS, payload);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PERSON_CATEGORY_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param personCategoryId
   * @returns {Promise<void>|Promise<T>}
   */
  personCategoryRemove({ commit }, personCategoryId) {
    if (!personCategoryId) {
      commit(C.SKI_SCHOOL_PERSON_CATEGORY_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_SCHOOL_PERSON_CATEGORY_REMOVE_PENDING);
    return SkiSchoolService.deletePersonCategory(personCategoryId)
      .then(() => {
        commit(C.SKI_SCHOOL_PERSON_CATEGORY_REMOVE_SUCCESS, personCategoryId);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PERSON_CATEGORY_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  levelsByIdLoad({ commit }, seasonId) {
    commit(C.SKI_SCHOOL_LEVELS_LOAD_PENDING);
    return SkiSchoolService.getLevelsById(seasonId)
      .then((res) => {
        const levels = map(res.data, level => ({
          id: level.id,
          skiSchoolSeasonId: level.skiSchoolSeasonId,
          name: level.name,
          externalName: level.externalName,
          priority: level.priority,
        }));
        commit(C.SKI_SCHOOL_LEVELS_LOAD_SUCCESS, levels);
      })
      .catch((error) => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_SCHOOL_LEVELS_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_SCHOOL_LEVELS_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   */
  levelAddNew({ commit }) {
    commit(C.SKI_SCHOOL_LEVEL_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setLevel({ commit }, payload) {
    commit(C.SKI_SCHOOL_LEVELS_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  levelCreate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_LEVEL_CREATE_PENDING);
    return SkiSchoolService.createLevel(payload)
      .then(() => {
        commit(C.SKI_SCHOOL_LEVEL_CREATE_SUCCESS);
        dispatch('levelsByIdLoad', payload.skiSchoolSeasonId);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_LEVEL_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  levelSave({ commit }, payload) {
    commit(C.SKI_SCHOOL_LEVEL_SAVE_PENDING);
    return SkiSchoolService.saveLevel(payload)
      .then(() => {
        commit(C.SKI_SCHOOL_LEVEL_SAVE_SUCCESS, payload);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_LEVEL_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param levelId
   * @returns {Promise<void>|Promise<T>}
   */
  levelRemove({ commit }, levelId) {
    if (!levelId) {
      commit(C.SKI_SCHOOL_LEVEL_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_SCHOOL_LEVEL_REMOVE_PENDING);
    return SkiSchoolService.deleteLevel(levelId)
      .then(() => {
        commit(C.SKI_SCHOOL_LEVEL_REMOVE_SUCCESS, levelId);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_LEVEL_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  pricesBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_SCHOOL_PRICES_LOAD_PENDING);
    return SkiSchoolService.getPricesBySeasonId(seasonId)
      .then((res) => {
        const prices = map(res.data, price => ({
          id: price.id,
          skiSchoolSeasonId: price.skiSchoolSeasonId,
          skiSchoolLessonTypeId: price.skiSchoolLessonTypeId,
          skiSchoolSkiTypeId: price.skiSchoolSkiTypeId,
          numberOfPersons: price.numberOfPersons,
          days: price.days,
          durationMinutes: price.durationMinutes,
          price: price.price,
          commissionRatio: price.commissionRatio,
          contributionRatio: price.contributionRatio,
          webActive: price.webActive,
          priceStartDays: price.priceStartDays,
          priceCombinations: price.priceCombinations,
        }));
        commit(C.SKI_SCHOOL_PRICES_LOAD_SUCCESS, prices);
      })
      .catch((error) => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_SCHOOL_PRICES_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_SCHOOL_PRICES_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param newPrice
   * @returns {Promise<T>}
   */
  priceCreate({ commit, dispatch }, newPrice) {
    commit(C.SKI_SCHOOL_PRICE_CREATE_PENDING);
    return SkiSchoolService.createPrice(newPrice)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_CREATE_SUCCESS);
        dispatch('pricesBySeasonIdLoad', newPrice.skiSchoolSeasonId);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PRICE_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setPrice({ commit }, payload) {
    commit(C.SKI_SCHOOL_PRICE_SET, payload);
  },

  /**
   * @param commit
   * @param price
   * @returns {Promise<T>}
   */
  priceSave({ commit }, price) {
    commit(C.SKI_SCHOOL_PRICE_SAVE_PENDING);
    return SkiSchoolService.savePrice(price)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_SAVE_SUCCESS, price);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PRICE_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param priceId
   * @returns {Promise<T>}
   */
  priceDelete({ commit }, priceId) {
    commit(C.SKI_SCHOOL_PRICE_REMOVE_PENDING);
    return SkiSchoolService.deletePrice(priceId)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_REMOVE_SUCCESS, priceId);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PRICE_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param priceId
   * @param weekdayNumber
   * @returns {Promise<T>}
   */
  startDayOnPriceSave({ commit }, { priceId, weekdayNumber }) {
    commit(C.SKI_SCHOOL_PRICE_START_DAY_SAVE_PENDING);
    return SkiSchoolService.saveStartDayOnPrice(priceId, weekdayNumber)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_START_DAY_SAVE_SUCCESS, { priceId, weekdayNumber });
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_PRICE_START_DAY_SAVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param priceId
   * @param weekdayNumber
   * @returns {Promise<T>}
   */
  startDayOnPriceDelete({ commit }, { priceId, weekdayNumber }) {
    commit(C.SKI_SCHOOL_PRICE_START_DAY_DELETE_PENDING);
    return SkiSchoolService.deleteStartDayOnPrice(priceId, weekdayNumber)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_START_DAY_DELETE_SUCCESS, { priceId, weekdayNumber });
      })
      .catch(() => {
        commit(C.SKI_SCHOOL_PRICE_START_DAY_DELETE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  priceCombinationAdd({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_PRICE_COMBINATION_SAVE_PENDING);
    return SkiSchoolService.savePriceCombination(payload.priceId, payload.newPriceCombination)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_COMBINATION_SAVE_SUCCESS, payload);
        dispatch('pricesBySeasonIdLoad', payload.seasonId);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PRICE_COMBINATION_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param priceId
   * @param priceCombinationId
   * @returns {Promise<T>}
   */
  priceCombinationDelete({ commit }, { priceId, priceCombinationId }) {
    commit(C.SKI_SCHOOL_PRICE_COMBINATION_DELETE_PENDING);
    return SkiSchoolService.deletePriceCombination(priceId, priceCombinationId)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_COMBINATION_DELETE_SUCCESS, { priceId, priceCombinationId });
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PRICE_COMBINATION_DELETE_FAILURE, error.response.data);
      });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/school/actions.js